import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Settings, Save, Users, Star, Plus, Edit, Trash2, Shield, Eye, EyeOff, ExternalLink, Settings2, GraduationCap, User, UserCog, Check, X, CalendarDays, Copy, Bell, Mail, Clock, Send } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Avatar, AvatarImage, AvatarFallback } from "../../components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "../../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Switch } from "../../components/ui/switch";
import { API, useAuth } from "../../App";
import AdminSettings from "../AdminSettings";


// Lesson Reminders Configuration
const LessonRemindersSettings = () => {
  const [settings, setSettings] = useState({
    lesson_reminders_enabled: true,
    lesson_reminder_hours: 24
  });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const [settingsRes, statusRes] = await Promise.all([
        axios.get(`${API}/reminders/settings`, { withCredentials: true }),
        axios.get(`${API}/reminders/status`, { withCredentials: true })
      ]);
      setSettings(settingsRes.data);
      setStatus(statusRes.data);
    } catch (err) {
      console.error("Error fetching reminder settings:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleToggle = async (enabled) => {
    try {
      await axios.patch(`${API}/reminders/settings`, { lesson_reminders_enabled: enabled }, { withCredentials: true });
      setSettings(prev => ({ ...prev, lesson_reminders_enabled: enabled }));
      toast.success(enabled ? "Lembretes ativados" : "Lembretes desativados");
    } catch (err) {
      console.error("Error updating reminder settings:", err);
      toast.error("Erro ao atualizar");
    }
  };

  const handleSendNow = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/reminders/send`, {}, { withCredentials: true });
      toast.success(`Enviados ${res.data.sent || 0} lembretes`);
      fetchSettings(); // Refresh status
    } catch (err) {
      console.error("Error sending reminders:", err);
      toast.error("Erro ao enviar lembretes");
    } finally {
      setSending(false);
    }
  };

  if (loading) return <p className="text-center text-[#94A3B8] py-8">A carregar...</p>;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[#3d5a63]">Lembretes Automáticos</h2>
        <p className="text-sm text-[#94A3B8]">Configura os lembretes enviados automaticamente aos alunos</p>
      </div>

      {/* Status Card */}
      {status && (
        <Card className="border-0 shadow-sm bg-gradient-to-r from-blue-50 to-cyan-50">
          <CardContent className="p-5">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${settings.lesson_reminders_enabled ? 'bg-green-100' : 'bg-slate-100'}`}>
                  <Clock className={`h-6 w-6 ${settings.lesson_reminders_enabled ? 'text-green-600' : 'text-slate-400'}`} />
                </div>
                <div>
                  <p className="font-semibold text-[#3d5a63]">
                    {settings.lesson_reminders_enabled ? 'Sistema Ativo' : 'Sistema Desativado'}
                  </p>
                  <p className="text-sm text-[#94A3B8]">
                    Verifica a cada hora e envia lembretes 24h antes das aulas
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-2xl font-bold text-[#3d5a63]">{status.sent_today || 0}</p>
                  <p className="text-xs text-[#94A3B8]">enviados hoje</p>
                </div>
                <div className="w-px h-10 bg-slate-200" />
                <div className="text-right">
                  <p className="text-2xl font-bold text-[#6697a4]">{status.pending_reminders || 0}</p>
                  <p className="text-xs text-[#94A3B8]">pendentes</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Settings Card */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-5 space-y-6">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-[#6697a4]/10 flex items-center justify-center">
                <Mail className="h-5 w-5 text-[#6697a4]" />
              </div>
              <div>
                <p className="font-medium text-[#3d5a63]">Lembretes de Aula</p>
                <p className="text-sm text-[#94A3B8]">Enviar email aos alunos 24 horas antes da aula</p>
              </div>
            </div>
            <Switch
              checked={settings.lesson_reminders_enabled}
              onCheckedChange={handleToggle}
              data-testid="reminder-toggle"
            />
          </div>

          {/* Info Box */}
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
            <h4 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
              <Bell className="h-4 w-4" /> Como funciona
            </h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• O sistema verifica automaticamente a cada hora</li>
              <li>• Envia lembretes para aulas que começam nas próximas 24 horas</li>
              <li>• Cada aluno recebe apenas um lembrete por inscrição</li>
              <li>• Inclui data, hora, local e o que trazer</li>
            </ul>
          </div>

          {/* Manual Send Button */}
          <div className="pt-4 border-t">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-[#3d5a63]">Enviar Agora</p>
                <p className="text-sm text-[#94A3B8]">Enviar lembretes pendentes manualmente</p>
              </div>
              <Button
                onClick={handleSendNow}
                disabled={sending || !settings.lesson_reminders_enabled}
                variant="outline"
                className="border-[#6697a4] text-[#6697a4] hover:bg-[#6697a4]/5"
                data-testid="send-reminders-btn"
              >
                <Send className="h-4 w-4 mr-2" />
                {sending ? "A enviar..." : "Enviar Lembretes"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};


const AdminStaff = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, { withCredentials: true });
      // Filter to show only staff (professors and admins)
      const staff = response.data.filter(u => u.role === "professor" || u.role === "admin");
      setUsers(staff);
    } catch (error) {
      toast.error("Erro ao carregar equipa");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleChangeRole = async (userId, newRole) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/role`, { role: newRole }, { withCredentials: true });
      toast.success(`Role alterado para ${newRole}`);
      fetchUsers();
    } catch (error) {
      toast.error("Erro ao alterar role");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-[#3d5a63] mb-2">Equipa</h1>
        <p className="text-[#475569]">Professores e administradores da escola</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-[#6697a4] border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : users.length === 0 ? (
        <Card className="bg-white border-0 shadow-sm rounded-xl">
          <CardContent className="p-12 text-center">
            <GraduationCap className="h-12 w-12 text-[#94A3B8] mx-auto mb-4" />
            <p className="text-[#475569]">Sem membros na equipa</p>
            <p className="text-sm text-[#94A3B8]">Promove alunos a professor na secção Alunos</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {users.map((user) => (
            <Card key={user.user_id} className="bg-white border-0 shadow-sm rounded-xl">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={user.picture} />
                      <AvatarFallback className={user.role === "admin" ? "bg-[#3d5a63] text-white" : "bg-[#6697a4] text-white"}>
                        {user.name?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-[#3d5a63]">{user.name}</p>
                      <p className="text-sm text-[#94A3B8]">{user.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge className={user.role === "admin" ? "bg-[#3d5a63] text-white border-0" : "bg-[#6697a4] text-white border-0"}>
                      {user.role === "admin" ? "Administrador" : "Professor"}
                    </Badge>
                    {user.role === "professor" && (
                      <div className="text-center">
                        <p className="text-xl font-bold text-[#FF7F50]">{user.review_count || 0}</p>
                        <p className="text-xs text-[#94A3B8]">Reviews dadas</p>
                      </div>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="outline">
                          <UserCog className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        {user.role === "professor" && (
                          <>
                            <DropdownMenuItem onClick={() => handleChangeRole(user.user_id, "admin")}>
                              <User className="mr-2 h-4 w-4" /> Promover a Admin
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleChangeRole(user.user_id, "student")}>
                              <Users className="mr-2 h-4 w-4" /> Voltar a Aluno
                            </DropdownMenuItem>
                          </>
                        )}
                        {user.role === "admin" && (
                          <>
                            <DropdownMenuItem onClick={() => handleChangeRole(user.user_id, "professor")}>
                              <GraduationCap className="mr-2 h-4 w-4" /> Tornar Professor
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleChangeRole(user.user_id, "student")}>
                              <Users className="mr-2 h-4 w-4" /> Tornar Aluno
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Admin Reviews Component

const AdminTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ author_name: "", text: "", rating: 5, published: true });
  const [googleRating, setGoogleRating] = useState(5.0);
  const [googleCount, setGoogleCount] = useState(291);

  const fetchTestimonials = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/testimonials`, { withCredentials: true });
      setTestimonials(res.data);
    } catch (err) { 
      console.error("Error fetching testimonials:", err);
      toast.error("Erro ao carregar testemunhos"); 
    }
    finally { setLoading(false); }
  }, []);

  const fetchGoogleInfo = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/testimonials`);
      setGoogleRating(res.data.google_rating || 5.0);
      setGoogleCount(res.data.google_review_count || 291);
    } catch (err) {
      console.error("Error fetching Google info:", err);
    }
  }, []);

  useEffect(() => { fetchTestimonials(); fetchGoogleInfo(); }, [fetchTestimonials, fetchGoogleInfo]);

  const handleSave = async () => {
    try {
      if (editItem) {
        await axios.put(`${API}/admin/testimonials/${editItem.testimonial_id}`, form, { withCredentials: true });
        toast.success("Testemunho atualizado");
      } else {
        await axios.post(`${API}/admin/testimonials`, form, { withCredentials: true });
        toast.success("Testemunho adicionado");
      }
      setShowAdd(false); setEditItem(null);
      setForm({ author_name: "", text: "", rating: 5, published: true });
      fetchTestimonials();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar este testemunho?")) return;
    try {
      await axios.delete(`${API}/admin/testimonials/${id}`, { withCredentials: true });
      toast.success("Eliminado");
      fetchTestimonials();
    } catch { toast.error("Erro ao eliminar"); }
  };

  const handleTogglePublish = async (t) => {
    try {
      await axios.put(`${API}/admin/testimonials/${t.testimonial_id}`, { published: !t.published }, { withCredentials: true });
      fetchTestimonials();
    } catch { toast.error("Erro"); }
  };

  const handleGoogleUpdate = async () => {
    try {
      await axios.put(`${API}/admin/google-rating`, { rating: googleRating, count: googleCount }, { withCredentials: true });
      toast.success("Avaliação Google atualizada");
    } catch { toast.error("Erro"); }
  };

  const openEdit = (t) => {
    setEditItem(t);
    setForm({ author_name: t.author_name, text: t.text, rating: t.rating, published: t.published });
    setShowAdd(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#3d5a63]">Testemunhos Google</h2>
          <p className="text-sm text-[#94A3B8]">Gerir testemunhos do Google exibidos na Landing Page (máx. 10)</p>
        </div>
        <Button onClick={() => { setEditItem(null); setForm({ author_name: "", text: "", rating: 5, published: true }); setShowAdd(true); }} className="bg-[#6697a4] hover:bg-[#3d5a63]" disabled={testimonials.length >= 10} data-testid="add-testimonial-btn">
          <Plus className="mr-2 h-4 w-4" /> Adicionar
        </Button>
      </div>

      {/* Google Rating Settings */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center gap-2">
              <svg className="h-5 w-5 text-[#4285F4]" viewBox="0 0 24 24" fill="currentColor"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              <span className="font-semibold text-[#3d5a63]">Avaliação Google</span>
            </div>
            <div className="flex items-center gap-3 flex-1">
              <div className="flex items-center gap-2">
                <Label className="text-xs text-[#94A3B8]">Nota:</Label>
                <Input type="number" step="0.1" min="0" max="5" value={googleRating} onChange={e => setGoogleRating(e.target.value === '' ? '' : parseFloat(e.target.value))} onBlur={e => setGoogleRating(parseFloat(e.target.value) || 0)} className="w-20 h-8 text-sm" data-testid="google-rating-input" />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-[#94A3B8]">N.º Reviews:</Label>
                <Input type="number" min="0" value={googleCount} onChange={e => setGoogleCount(e.target.value === '' ? '' : parseInt(e.target.value))} onBlur={e => setGoogleCount(parseInt(e.target.value) || 0)} className="w-24 h-8 text-sm" data-testid="google-count-input" />
              </div>
              <Button size="sm" variant="outline" onClick={handleGoogleUpdate} data-testid="save-google-rating">Guardar</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Testimonials List */}
      {loading ? <p className="text-center text-[#94A3B8]">A carregar...</p> : (
        <div className="space-y-3">
          {testimonials.map((t) => (
            <Card key={t.testimonial_id} className={`border-0 shadow-sm ${!t.published ? 'opacity-60' : ''}`} data-testid={`testimonial-item-${t.testimonial_id}`}>
              <CardContent className="p-4 flex items-start gap-4">
                <div className="w-10 h-10 bg-[#6697a4] rounded-full flex items-center justify-center text-white font-bold shrink-0">
                  {t.author_name?.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-[#3d5a63]">{t.author_name}</span>
                    <div className="flex gap-0.5">
                      {[1,2,3,4,5].map(s => (
                        <svg key={s} className={`h-3.5 w-3.5 ${s <= t.rating ? "text-[#F59E0B]" : "text-gray-200"}`} fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                    {!t.published && <Badge variant="outline" className="text-xs">Oculto</Badge>}
                  </div>
                  <p className="text-sm text-[#475569] line-clamp-2">{t.text}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => handleTogglePublish(t)} title={t.published ? "Ocultar" : "Publicar"} data-testid={`toggle-pub-${t.testimonial_id}`}>
                    {t.published ? <X className="h-4 w-4" /> : <Check className="h-4 w-4" />}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(t)} data-testid={`edit-testimonial-${t.testimonial_id}`}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(t.testimonial_id)} data-testid={`delete-testimonial-${t.testimonial_id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {testimonials.length === 0 && <p className="text-center text-[#94A3B8] py-8">Nenhum testemunho. Adicione reviews do Google.</p>}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editItem ? "Editar Testemunho" : "Adicionar Testemunho"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Nome do autor</Label>
              <Input value={form.author_name} onChange={e => setForm({...form, author_name: e.target.value})} placeholder="Nome como aparece no Google" data-testid="testimonial-author-input" />
            </div>
            <div>
              <Label>Texto da review</Label>
              <Textarea value={form.text} onChange={e => setForm({...form, text: e.target.value})} placeholder="Texto da review do Google..." rows={4} data-testid="testimonial-text-input" />
            </div>
            <div>
              <Label>Avaliação</Label>
              <div className="flex gap-1 mt-1">
                {[1,2,3,4,5].map(s => (
                  <button key={s} type="button" onClick={() => setForm({...form, rating: s})} data-testid={`testimonial-star-${s}`}>
                    <Star className={`h-7 w-7 ${s <= form.rating ? "text-[#F59E0B] fill-current" : "text-[#E2E8F0]"}`} />
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancelar</Button></DialogClose>
            <Button onClick={handleSave} className="bg-[#6697a4] hover:bg-[#3d5a63]" data-testid="save-testimonial-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// Google Calendar Settings
const GoogleCalendarSettings = () => {
  const [settings, setSettings] = useState({ google_client_id: "", google_client_secret: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/settings`, { withCredentials: true });
      setSettings({
        google_client_id: res.data.google_client_id || "",
        google_client_secret: res.data.google_client_secret || "",
      });
    } catch (err) { 
      console.error("Error fetching Google Calendar settings:", err);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/settings`, settings, { withCredentials: true });
      toast.success("Credenciais Google Calendar guardadas");
      fetchSettings();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao guardar"); }
    finally { setSaving(false); }
  };

  const redirectUri = `${window.location.origin}/api/oauth/calendar/callback`;

  if (loading) return <p className="text-center text-[#94A3B8] py-8">A carregar...</p>;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[#3d5a63]">Google Calendar</h2>
        <p className="text-sm text-[#94A3B8]">Configure a integração para exportar aulas e marcações para o Google Calendar</p>
      </div>

      {/* Setup Instructions */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-5">
          <h3 className="font-semibold text-[#3d5a63] mb-3 flex items-center gap-2">
            <Settings2 className="h-4 w-4" /> Como configurar
          </h3>
          <ol className="space-y-2 text-sm text-[#475569]">
            <li className="flex gap-2">
              <span className="font-bold text-[#6697a4] shrink-0">1.</span>
              <span>Acede ao <a href="https://console.cloud.google.com" target="_blank" rel="noreferrer" className="text-[#6697a4] underline">Google Cloud Console</a> e cria um projeto (ou usa um existente)</span>
            </li>
            <li className="flex gap-2">
              <span className="font-bold text-[#6697a4] shrink-0">2.</span>
              <span>Vai a <strong>APIs & Services &gt; Library</strong> e ativa a <strong>Google Calendar API</strong></span>
            </li>
            <li className="flex gap-2">
              <span className="font-bold text-[#6697a4] shrink-0">3.</span>
              <span>Vai a <strong>OAuth consent screen</strong>, escolhe <strong>External</strong>, preenche o nome da app e emails</span>
            </li>
            <li className="flex gap-2">
              <span className="font-bold text-[#6697a4] shrink-0">4.</span>
              <span>Em <strong>Scopes</strong>, adiciona: <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">https://www.googleapis.com/auth/calendar</code></span>
            </li>
            <li className="flex gap-2">
              <span className="font-bold text-[#6697a4] shrink-0">5.</span>
              <span>Em <strong>Test users</strong>, adiciona o teu email Google</span>
            </li>
            <li className="flex gap-2">
              <span className="font-bold text-[#6697a4] shrink-0">6.</span>
              <span>Vai a <strong>Credentials &gt; Create Credentials &gt; OAuth client ID</strong> (Web application)</span>
            </li>
            <li className="flex gap-2">
              <span className="font-bold text-[#6697a4] shrink-0">7.</span>
              <div>
                <span>Adiciona os seguintes URIs:</span>
                <div className="mt-1 space-y-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-[#94A3B8]">Authorized JavaScript origins:</span>
                    <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">{window.location.origin}</code>
                    <button onClick={() => { navigator.clipboard.writeText(window.location.origin); toast.success("Copiado!"); }} className="p-0.5 hover:bg-slate-100 rounded">
                      <Copy className="h-3 w-3 text-[#94A3B8]" />
                    </button>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-[#94A3B8]">Authorized redirect URIs:</span>
                    <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">{redirectUri}</code>
                    <button onClick={() => { navigator.clipboard.writeText(redirectUri); toast.success("Copiado!"); }} className="p-0.5 hover:bg-slate-100 rounded">
                      <Copy className="h-3 w-3 text-[#94A3B8]" />
                    </button>
                  </div>
                </div>
              </div>
            </li>
            <li className="flex gap-2">
              <span className="font-bold text-[#6697a4] shrink-0">8.</span>
              <span>Copia o <strong>Client ID</strong> e <strong>Client Secret</strong> e cola abaixo</span>
            </li>
          </ol>
        </CardContent>
      </Card>

      {/* Credentials Form */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-5 space-y-4">
          <h3 className="font-semibold text-[#3d5a63]">Credenciais OAuth</h3>
          <div>
            <Label className="text-sm">Google Client ID</Label>
            <Input
              value={settings.google_client_id}
              onChange={(e) => setSettings({ ...settings, google_client_id: e.target.value })}
              placeholder="xxxx.apps.googleusercontent.com"
              className="mt-1 font-mono text-sm"
              data-testid="google-client-id-input"
            />
          </div>
          <div>
            <Label className="text-sm">Google Client Secret</Label>
            <div className="relative mt-1">
              <Input
                type={showSecret ? "text" : "password"}
                value={settings.google_client_secret}
                onChange={(e) => setSettings({ ...settings, google_client_secret: e.target.value })}
                placeholder="GOCSPX-xxxx"
                className="font-mono text-sm pr-10"
                data-testid="google-client-secret-input"
              />
              <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 p-1" onClick={() => setShowSecret(!showSecret)}>
                {showSecret ? <EyeOff className="h-4 w-4 text-[#94A3B8]" /> : <Eye className="h-4 w-4 text-[#94A3B8]" />}
              </button>
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-[#6697a4] hover:bg-[#567f8a]" data-testid="save-google-creds-btn">
            <Save className="h-4 w-4 mr-1" /> {saving ? "A guardar..." : "Guardar Credenciais"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};


// Admin Email Notification Preferences
const AdminNotificationPreferences = () => {
  const [preferences, setPreferences] = useState({
    notify_enrollment: false,
    notify_pack_purchase: false,
    notify_subscription: false,
    notify_cancellation: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchPreferences = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/notification-preferences`, { withCredentials: true });
      setPreferences({
        notify_enrollment: res.data.notify_enrollment || false,
        notify_pack_purchase: res.data.notify_pack_purchase || false,
        notify_subscription: res.data.notify_subscription || false,
        notify_cancellation: res.data.notify_cancellation || false
      });
    } catch (err) {
      console.error("Error fetching notification preferences:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  const handleToggle = async (key) => {
    const newValue = !preferences[key];
    setPreferences(prev => ({ ...prev, [key]: newValue }));
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/notification-preferences`, { [key]: newValue }, { withCredentials: true });
      toast.success("Preferência atualizada");
    } catch (err) {
      // Revert on error
      setPreferences(prev => ({ ...prev, [key]: !newValue }));
      toast.error("Erro ao atualizar preferência");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-center text-[#94A3B8] py-8">A carregar...</p>;

  const notificationOptions = [
    {
      key: "notify_enrollment",
      title: "Inscrições em aulas",
      description: "Receber email quando um aluno se inscreve numa aula",
      icon: Users
    },
    {
      key: "notify_cancellation",
      title: "Cancelamentos",
      description: "Receber email quando um aluno cancela uma inscrição",
      icon: X
    },
    {
      key: "notify_pack_purchase",
      title: "Compras de packs",
      description: "Receber email quando um aluno compra um pack de aulas",
      icon: Star
    },
    {
      key: "notify_subscription",
      title: "Novas subscrições",
      description: "Receber email quando um aluno inicia uma subscrição mensal",
      icon: CalendarDays
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[#3d5a63]">Notificações por Email</h2>
        <p className="text-sm text-[#94A3B8]">Escolhe que eventos te enviam um email de notificação</p>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-5">
          <div className="flex items-center gap-3 mb-6 p-4 bg-blue-50 rounded-lg">
            <Mail className="h-5 w-5 text-blue-600 shrink-0" />
            <p className="text-sm text-blue-800">
              As notificações são enviadas para o email associado à tua conta de admin. Cada admin pode configurar as suas próprias preferências.
            </p>
          </div>

          <div className="space-y-4">
            {notificationOptions.map((option) => (
              <div
                key={option.key}
                className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                data-testid={`notification-option-${option.key}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#6697a4]/10 flex items-center justify-center">
                    <option.icon className="h-5 w-5 text-[#6697a4]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#3d5a63]">{option.title}</p>
                    <p className="text-sm text-[#94A3B8]">{option.description}</p>
                  </div>
                </div>
                <Switch
                  checked={preferences[option.key]}
                  onCheckedChange={() => handleToggle(option.key)}
                  disabled={saving}
                  data-testid={`notification-toggle-${option.key}`}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-5">
          <h3 className="font-semibold text-[#3d5a63] mb-3 flex items-center gap-2">
            <Bell className="h-4 w-4" /> Nota
          </h3>
          <p className="text-sm text-[#475569]">
            Além dos emails, continuas a receber notificações na app para todos os eventos importantes. 
            Estas preferências apenas controlam os <strong>emails</strong> que recebes.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};


// Admin Auto-Schedule Configuration

const AdminSettingsPage = () => {
  const [tab, setTab] = useState("settings");
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        {[
          { key: "settings", label: "Geral", icon: Settings2 },
          { key: "notifications", label: "Notificações", icon: Bell },
          { key: "reminders", label: "Lembretes", icon: Clock },
          { key: "testimonials", label: "Testemunhos Google", icon: Star },
          { key: "staff", label: "Equipa & Roles", icon: GraduationCap },
          { key: "gcalendar", label: "Google Calendar", icon: CalendarDays },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              tab === t.key ? "bg-[#6697a4] text-white" : "bg-white text-[#475569] hover:bg-slate-50 border"
            }`} data-testid={`settings-tab-${t.key}`}
          >
            <t.icon className="h-4 w-4" />{t.label}
          </button>
        ))}
      </div>
      {tab === "settings" && <AdminSettings />}
      {tab === "notifications" && <AdminNotificationPreferences />}
      {tab === "reminders" && <LessonRemindersSettings />}
      {tab === "testimonials" && <AdminTestimonials />}
      {tab === "staff" && <AdminStaff />}
      {tab === "gcalendar" && <GoogleCalendarSettings />}
    </div>
  );
};

export default AdminSettingsPage;
